/**
 * Shared agreement money math for Summary, Preview, and consistency checks.
 */

export function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Parse YYYY-MM-DD from HTML date inputs as a local-calendar midnight.
 * `new Date("YYYY-MM-DD")` is UTC-based and can appear as the prior local day/month
 * in zones behind UTC, inflating inclusive month/year counts by one.
 */
export function parseBillingDateOnly(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr ?? "").trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const monthIndex = parseInt(m[2], 10) - 1;
  const d = parseInt(m[3], 10);
  if (
    monthIndex < 0 ||
    monthIndex > 11 ||
    d < 1 ||
    d > 31 ||
    !Number.isFinite(y)
  ) {
    return null;
  }
  const dt = new Date(y, monthIndex, d);
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getFullYear() !== y ||
    dt.getMonth() !== monthIndex ||
    dt.getDate() !== d
  ) {
    return null;
  }
  return dt;
}

/**
 * Other services line: show "Calculation: ₹unit × N months|years|students = ₹total".
 * Pass resolvedQty = null when billing period / student count is not yet set (student count: empty field).
 */
export function otherServiceCalculationExplain(unitRaw, calcType, resolvedQty) {
  const unit = safeNum(unitRaw);
  const uStr = unit.toFixed(2);
  const q = resolvedQty != null && Number.isFinite(Number(resolvedQty)) ? Number(resolvedQty) : null;

  if (calcType === "studentcount") {
    if (q == null) {
      return `₹${uStr} × no. of students (enter number of students in the agreement)`;
    }
    const total = Number((unit * q).toFixed(2));
    return `₹${uStr} × ${q} student${q === 1 ? "" : "s"} = ₹${total.toFixed(2)}`;
  }
  if (calcType === "yearwise") {
    if (q == null || q <= 0) {
      return `₹${uStr} × no. of years (enter billing From and To dates)`;
    }
    const total = Number((unit * q).toFixed(2));
    return `₹${uStr} × ${q} year${q === 1 ? "" : "s"} = ₹${total.toFixed(2)}`;
  }
  if (q == null || q <= 0) {
    return `₹${uStr} × no. of months (enter billing From and To dates)`;
  }
  const total = Number((unit * q).toFixed(2));
  return `₹${uStr} × ${q} month${q === 1 ? "" : "s"} = ₹${total.toFixed(2)}`;
}

/** API / FE may send services as dict or array (serialized rows). */
export function normalizeServices(raw) {
  if (!raw) return {};
  if (Array.isArray(raw)) {
    const o = {};
    raw.forEach((item) => {
      if (!item || !item.name) return;
      o[item.name] = {
        Amount: item.amount != null ? item.amount : item.Amount,
        GST: item.gst_percentage != null ? item.gst_percentage : item.GST,
        Description: item.description != null ? item.description : item.Description,
      };
    });
    return o;
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  return {};
}

/**
 * ERP licence subtotal before GST.
 * - "Number of students": students × amount (GST applies to this base).
 * - "Up to student": flat amount for the band; student count does not multiply the fee.
 * @param {object} d agreement fields (peramount, amountEntry, numberOfStudents, gst, …)
 * @param {number|null|undefined} studentCount optional override (e.g. billing dialog strength when ordering)
 */
export function erpAmountBeforeGst(d, studentCount = null) {
  const per = safeNum(d?.peramount);
  const fromAgreement = safeNum(d?.numberOfStudents);
  const n =
    studentCount != null && Number.isFinite(Number(studentCount))
      ? safeNum(studentCount)
      : fromAgreement;
  if (d?.amountEntry === "Number of students") {
    return per * n;
  }
  if (d?.amountEntry === "Up to student") {
    return per;
  }
  return 0;
}

export function erpTotalInclGst(d, studentCount = null) {
  const base = erpAmountBeforeGst(d, studentCount);
  const g = safeNum(d?.gst);
  return base + base * (g / 100);
}

/** ID card subtotal before GST: per-id amount × number of students. */
export function idCardAmountTotal(d, studentCount = null) {
  const perId = safeNum(d?.perId);
  const fromAgreement = safeNum(d?.numberOfStudents);
  const n =
    studentCount != null && Number.isFinite(Number(studentCount))
      ? safeNum(studentCount)
      : fromAgreement;
  if (perId <= 0 || n <= 0) return 0;
  return perId * n;
}

export function servicesTotalInclGst(servicesRaw) {
  const s = normalizeServices(servicesRaw);
  return Object.values(s).reduce((sum, meta) => {
    const amount = safeNum(meta?.Amount);
    const sgst = safeNum(meta?.GST);
    const gstAmt = sgst ? amount * (sgst / 100) : 0;
    return sum + amount + gstAmt;
  }, 0);
}

/** Sum of agreed digital feature prices (first value in each entry); renewal excluded from one-time total. */
export function digitalPricesTotal(digitalfeature, d) {
  if (!digitalfeature || typeof digitalfeature !== "object" || Array.isArray(digitalfeature)) return 0;
  return Object.values(digitalfeature).reduce((sum, v) => {
    let price = 0;
    if (Array.isArray(v)) price = safeNum(v[0]);
    else if (v && typeof v === "object") {
      if (v.total_price != null && v.total_price !== "") {
        price = safeNum(v.total_price);
      } else {
        const unit = safeNum(v.price);
        const calcType = v.calculation_type || "monthwise";
        
        let quantity = 1;
        if (calcType === "yearwise") {
          const from = v.billing_from ? parseBillingDateOnly(v.billing_from) : null;
          const to = v.billing_to ? parseBillingDateOnly(v.billing_to) : null;
          if (from && to && !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && to >= from) {
            let years = to.getFullYear() - from.getFullYear();
            if (to.getMonth() > from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() > from.getDate())) {
              years += 1;
            }
            quantity = years > 0 ? years : 1;
          } else {
            quantity = 1;
          }
        } else if (calcType === "studentcount") {
          quantity = safeNum(d?.numberOfStudents);
        } else {
          const from = v.billing_from ? parseBillingDateOnly(v.billing_from) : null;
          const to = v.billing_to ? parseBillingDateOnly(v.billing_to) : null;
          if (from && to && !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && to >= from) {
            const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
            quantity = months > 0 ? months : 1;
          }
        }
        price = unit * quantity;
      }
    }
    return sum + price;
  }, 0);
}

export function grandTotalAgreement(d) {
  const digital = digitalPricesTotal(d?.digitalfeature, d);
  return (
    erpTotalInclGst(d) +
    idCardAmountTotal(d) +
    servicesTotalInclGst(d?.services) +
    digital
  );
}

/**
 * One other-service (digital) line: amount and a short calculation note for previews.
 */
export function singleDigitalFeatureAmount(v, d) {
  if (v == null) return { amount: 0, note: "" };
  if (Array.isArray(v)) {
    const unit = safeNum(v[0]);
    return {
      amount: unit,
      note: `Calculation: ₹${unit.toFixed(2)} × 1 = ₹${unit.toFixed(2)}`,
    };
  }
  if (v && typeof v === "object") {
    const unit = safeNum(v.price);
    const calcType = v.calculation_type || "monthwise";
    const pq =
      v.quantity != null && v.quantity !== "" && Number.isFinite(Number(v.quantity))
        ? Number(v.quantity)
        : null;

    let quantity = 1;
    let resolvedForExplain = null;

    // Prefer persisted quantity when present so summary dialogs match drafted API rows
    // (those rows usually include total_price, which previously hid the breakdown).
    if (calcType === "studentcount") {
      if (pq != null) {
        quantity = pq;
        resolvedForExplain = pq;
      } else {
        const raw = d?.numberOfStudents;
        const hasEntry = raw !== null && raw !== undefined && String(raw).trim() !== "";
        quantity = hasEntry ? safeNum(raw) : 0;
        resolvedForExplain = hasEntry ? quantity : null;
      }
    } else if ((calcType === "yearwise" || calcType === "monthwise") && pq != null && pq > 0) {
      quantity = pq;
      resolvedForExplain = pq;
    } else if (calcType === "yearwise") {
      const from = v.billing_from ? parseBillingDateOnly(v.billing_from) : null;
      const to = v.billing_to ? parseBillingDateOnly(v.billing_to) : null;
      if (from && to && !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && to >= from) {
        let years = to.getFullYear() - from.getFullYear();
        if (to.getMonth() > from.getMonth() || (to.getMonth() === from.getMonth() && to.getDate() > from.getDate())) {
          years += 1;
        }
        quantity = years > 0 ? years : 1;
        resolvedForExplain = quantity;
      } else {
        quantity = 1;
        resolvedForExplain = null;
      }
    } else {
      const from = v.billing_from ? parseBillingDateOnly(v.billing_from) : null;
      const to = v.billing_to ? parseBillingDateOnly(v.billing_to) : null;
      if (from && to && !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && to >= from) {
        const months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth()) + 1;
        quantity = months > 0 ? months : 1;
        resolvedForExplain = quantity;
      } else {
        quantity = 1;
        resolvedForExplain = null;
      }
    }

    const hasTotalPrice = v.total_price != null && v.total_price !== "";
    const amount = hasTotalPrice ? safeNum(v.total_price) : Number((unit * quantity).toFixed(2));
    let note = otherServiceCalculationExplain(unit, calcType, resolvedForExplain);
    if (
      hasTotalPrice &&
      resolvedForExplain == null &&
      (calcType === "monthwise" || calcType === "yearwise") &&
      !(v.billing_from && String(v.billing_from).trim() && v.billing_to && String(v.billing_to).trim())
    ) {
      note = `Agreed line total: ₹${amount.toFixed(2)}`;
    }

    return { amount, note };
  }
  return { amount: 0, note: "" };
}

/** Order form number: EB + ddmmyyyyhhmmss (local time). */
export function formatOrderFormNumber(date = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `EB${p(date.getDate())}${p(date.getMonth() + 1)}${date.getFullYear()}${p(date.getHours())}${p(date.getMinutes())}${p(date.getSeconds())}`;
}
